import { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router';
import { ChevronDown, LogOut } from 'lucide-react';
import Breadcrumb from './Breadcrumb';
import Sidebar from './Sidebar';
import { useAuth } from '../context/useAuth';
import { getInitials } from '../utils/stringUtils';
import './Layout.css';

export default function Layout() {
  // Initialize sidebar state from localStorage, default to true (expanded)
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarExpanded');
    return saved !== null ? saved === 'true' : true;
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();

  // The dashboard is a full-width overview — it drops the shared content max-width
  // so it fills the screen at any size (other pages keep the capped reading width).
  const location = useLocation();
  const isFullWidth = location.pathname === '/' || location.pathname === '/dashboard';

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarExpanded', sidebarOpen.toString());
  }, [sidebarOpen]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
  };

  const displayName = user?.displayName || user?.email || 'User';
  const initials = getInitials(displayName);

  return (
    <div className="app-layout">
      <header className="app-header">
        <div className="header-left">
          <div className="brand">Examinetics</div>
        </div>
        <div className="header-center">
          <Breadcrumb />
        </div>
        <div className="header-right">
          {user && (
            <div className="user-dropdown" ref={dropdownRef}>
              <button
                type="button"
                className="user-dropdown-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                <span className="user-avatar">{initials}</span>
                <span className="user-display-name">{displayName}</span>
                <ChevronDown
                  size={16}
                  className={`user-chevron ${dropdownOpen ? 'rotated' : ''}`}
                />
              </button>

              {dropdownOpen && (
                <div className="user-dropdown-menu">
                  <div className="user-dropdown-info">
                    <div className="user-dropdown-name">{displayName}</div>
                    <div className="user-dropdown-email">{user.email}</div>
                  </div>

                  {user.roles.length > 0 && (
                    <div className="user-dropdown-role-summary">
                      {user.roles.length} {user.roles.length === 1 ? 'permission' : 'permissions'}
                    </div>
                  )}

                  <div className="user-dropdown-divider" />

                  <button type="button" className="user-dropdown-logout" onClick={handleLogout}>
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      <div className="layout-container">
        <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
        <main className={`app-main ${sidebarOpen ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
          <div className={`content-wrapper${isFullWidth ? ' content-wrapper--full' : ''}`}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
